import { Navigate } from 'react-router-dom'
import PageLoader from '../components/PageLoader'
import { SESSION_STATUS, useSession } from '../lib/sessionContext'
import { isAdminSession, ROLES } from './navigation'

/**
 * What `/` means, decided from the session rather than guessed.
 *
 * The root used to match no route at all, so it fell through to the catch-all and was sent
 * to `/login`. For a signed-in visitor that page then resolved the session, found them
 * authenticated and called `goToLanding()` — a `window.location.assign('/Default')`, i.e. a
 * FULL PAGE RELOAD out of the SPA and back in again. The sign-in form was painted in the
 * middle of that, which is the flash: an app they were already signed into asking them to
 * sign in, then throwing the whole bundle away to land where it could have gone directly.
 *
 * The session is the only thing that decides, and until it has answered nothing is decided —
 * so a loader, not a guess. Rendering `/login` while the answer is in flight is what produced
 * the flash, and defaulting the other way would flash the shell at a signed-out visitor.
 */
export default function Landing() {
  const { status, session } = useSession()

  if (status === SESSION_STATUS.loading) return <PageLoader full />

  /* A reachable server that cannot answer is not "signed out" — the login page says so
     properly, and it is the only screen that can. */
  if (status !== SESSION_STATUS.authenticated) return <Navigate to="/login" replace />

  /* Admin is not a field on the session; it is whether any manager role is held. See
     `isAdminSession` — and note it is deliberately not "can see a menu entry", because the
     overview has no role guard and so is visible to every learner too. */
  if (session.roles.includes(ROLES.SystemManagers)) {
    return <Navigate to="/companies" replace />
  }

  return <Navigate to={isAdminSession(session) ? '/general' : '/portal/courses'} replace />
}
